import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Crown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionBanner = ({ onUpgrade }) => {
  const { user, getAuthHeader } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/user/subscription`, {
        headers: getAuthHeader()
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your premium subscription? You will return to the free tier (3 articles/day).')) {
      return;
    }

    setCancelling(true);
    try {
      await axios.post(`${API}/user/cancel-subscription`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Subscription cancelled. Back to free tier.');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading || !subscription) {
    return null;
  }

  // Hide all subscription banners in FREE_MODE
  if (subscription.free_mode || subscription.subscription_tier === 'free_unlimited') {
    return null;
  }

  // Show premium status banner
  if (subscription.subscription_tier === 'premium') {
    return (
      <div className="bg-gradient-to-r from-[#FFE66D] to-[#FF6B6B] border-2 border-black dark:border-white rounded-xl p-4 hard-shadow mb-8" data-testid="premium-status-banner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-6 h-6" strokeWidth={2.5} />
            <div>
              <p className="font-bold">Premium Member</p>
              <p className="text-sm">Unlimited articles • No ads • Cancel anytime</p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            disabled={cancelling}
            data-testid="cancel-subscription-button"
            className="bg-white text-black font-bold px-4 py-2 rounded-full border-2 border-black hover:bg-[#F0F0F0] transition-all text-sm disabled:opacity-50"
          >
            {cancelling ? 'Cancelling...' : 'Cancel Anytime'}
          </button>
        </div>
      </div>
    );
  }

  const articlesLeft = 3 - (subscription.daily_articles_read || 0);

  if (articlesLeft <= 0) {
    return (
      <div className="bg-[#FF6B6B] border-2 border-black dark:border-white rounded-xl p-6 hard-shadow-lg mb-8" data-testid="limit-reached-banner">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-6 h-6 text-white" strokeWidth={2.5} />
              <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Merriweather, serif' }}>
                Daily Limit Reached!
              </h3>
            </div>
            <p className="text-white text-lg mb-4">
              You've read your 3 free articles today. Upgrade to Premium for unlimited access!
            </p>
            <button
              onClick={onUpgrade}
              data-testid="upgrade-from-banner"
              className="bg-white text-black font-bold px-6 py-3 rounded-full border-2 border-black hover:bg-[#F0F0F0] transition-all flex items-center gap-2"
            >
              <Crown className="w-5 h-5" strokeWidth={2.5} />
              Upgrade for $1/month
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFE66D] border-2 border-black dark:border-white rounded-xl p-4 hard-shadow mb-8" data-testid="articles-left-banner">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold">
            {articlesLeft} {articlesLeft === 1 ? 'article' : 'articles'} left today
          </p>
          <p className="text-xs text-gray-700">
            Upgrade to Premium for unlimited access
          </p>
        </div>
        <button
          onClick={onUpgrade}
          data-testid="quick-upgrade-button"
          className="bg-black text-white font-bold px-4 py-2 rounded-full text-sm hover:bg-[#2D2A26] transition-all flex items-center gap-1"
        >
          <Crown className="w-4 h-4" strokeWidth={2.5} />
          $1/mo
        </button>
      </div>
    </div>
  );
};

export default SubscriptionBanner;